<template>
  <NuxtLayout :name="layoutName" :navbar-color="layoutNavbarColor" :footer-color="layoutFooterColor">
    <div v-if="story">
      <section class="vlogs__hero">
        <HeroResourcesHub>
          <template #subheading>
            {{ $t('common.resources_hub_hero.vlogs.subheading') }}
          </template>
          <template #title>
            {{ $t('common.resources_hub_hero.vlogs.title') }}
          </template>
          <template #text>
            {{ $t('common.resources_hub_hero.vlogs.text') }}
          </template>
          <template #image>
            <img
              src="/images/resources-hub/vlogs-hub.png"
              alt="vlogs Hub"
              class="vlogs__image"
            />
          </template>
        </HeroResourcesHub>
        <div class="container">
          <SbResourcesHubNav />
        </div>
      </section>
      <VlogCardSection :vlogs="filteredVlogs" />
      <StoryblokComponent
        v-for="body in story.content.body"
        :key="body._uid"
        :blok="body"
      />
    </div>
  </NuxtLayout>
</template>

<script lang="ts" setup>
import type { SbVlog, Story } from '@/types'
const { locale } = useI18n()

const vlogsStore = useVlogsStore()
const { availableSeasons, filters } = storeToRefs(vlogsStore)

const config = useRuntimeConfig()
const storyblokApi = useStoryblokApi()

const [{ data: vlogs }, { story }] = await Promise.all([
  useAsyncData('vlogs', async () => {
    const vlogs = await storyblokApi.get('cdn/stories', {
      version: config.public.storyblokApiVersion as 'draft' | 'published',
      starts_with: 'vlogs/',
      language: locale.value,
      per_page: 100,
      filter_query: {
        component: {
          in: 'vlog',
        },
      },
    })

    return vlogs.data.stories
  }),

  useFetchStory('vlogs'),
])
const { layoutName, layoutNavbarColor, layoutFooterColor } = useLayout<any>(story)

const filteredVlogs = computed(() => {
  if (!vlogs.value) {
    return []
  }

  if (!Object.values(filters.value.selectedSeasons).some((value) => value)) {
    return vlogs.value
  }

  const filteredVlogs = [...vlogs.value]
  const selectedSeasons = Object.keys(filters.value.selectedSeasons).filter(
    (season) =>
      filters.value.selectedSeasons[
        season as keyof typeof filters.value.selectedSeasons
      ],
  )

  return filteredVlogs.filter((vlog: SbVlog) =>
    selectedSeasons.includes(vlog.content.season),
  )
})

onMounted(() => {
  availableSeasons.value = [
    ...new Set(vlogs.value.map((vlog: Story<SbVlog>) => vlog.content.season)),
  ] as string[]
})

useCustomHead(story)
</script>

<style lang="scss" scoped>
.vlogs {
  &__hero {
    padding: 80px 0 48px 0;
  }

  &__image {
    width: 100%;
    max-width: 330px;
    margin-left: auto;
    margin-right: 0;

    @media (min-width: $breakpoint-lg) {
      position: absolute;
      right: -30px;
      top: 0;
    }
  }
}
</style>
